import type { LiveParticipantOption } from "../types.js";
import { normalizeName } from "./name-normalize.js";

export interface LiveParticipantInput {
  participantId?: string;
  participantUUID: string;
  screenName: string;
  /** 'host' | 'coHost' | 'attendee' | 'panelist'. Casing varies by SDK surface. */
  role?: string;
}

export interface LiveIndex {
  byId: Map<string, LiveParticipantOption>;
  byName: Map<string, LiveParticipantOption>;
  byEmail: Map<string, LiveParticipantOption>;
  byNormalizedName: Map<string, LiveParticipantOption>;
  byUuid: Map<string, LiveParticipantOption>;
  raw: LiveParticipantOption[];
  /** UUIDs of everyone running the meeting — see isOperatorRole(). */
  operatorUuids: Set<string>;
}

/**
 * True for the people who run the meeting rather than attend it. Zoom spells
 * this 'coHost' from getUserContext and 'cohost' from onParticipantChange, so
 * compare lowercased.
 */
export function isOperatorRole(role: string | undefined): boolean {
  const normalized = (role ?? "").toLowerCase();
  return normalized === "host" || normalized === "cohost";
}

/** Builds every lookup tier used by resolveLiveEntry() from one roster read. */
export function buildLiveIndex(
  participants: LiveParticipantInput[],
  knownEmailsByUuid: Map<string, string>,
): LiveIndex {
  const index: LiveIndex = {
    byId: new Map(),
    byName: new Map(),
    byEmail: new Map(),
    byNormalizedName: new Map(),
    byUuid: new Map(),
    raw: [],
    operatorUuids: new Set(),
  };
  for (const p of participants) {
    const entry = { uuid: p.participantUUID, screenName: p.screenName };
    if (p.participantId) index.byId.set(p.participantId, entry);
    if (p.screenName) {
      index.byName.set(p.screenName.trim().toLowerCase(), entry);
      const normalized = normalizeName(p.screenName);
      if (normalized) index.byNormalizedName.set(normalized, entry);
    }
    index.byUuid.set(p.participantUUID, entry);
    const knownEmail = knownEmailsByUuid.get(p.participantUUID);
    if (knownEmail) index.byEmail.set(knownEmail, entry);
    if (isOperatorRole(p.role)) index.operatorUuids.add(p.participantUUID);
    index.raw.push(entry);
  }
  return index;
}
